/**
 * member-promotion-cozy.js
 * 用途: 积分兑换组件交互逻辑
 *   - 登录状态检测与 Banner 切换
 *   - 获取用户积分信息
 *   - 兑换产品列表数据获取与渲染
 *   - 弹框管理（规则、积分详情、兑换记录、兑换确认、积分不足）
 *   - 积分兑换接口调用
 *   - Toast 轻提示
 * 依赖: momcozy-login.liquid（已渲染 login-dialog 组件）
 */
(function () {
  "use strict";

  // --------------------------------------------------------------------------
  // 防重入保护：脚本被多次引入（section 重复 / 模板叠加）时，仅执行一次 init
  // --------------------------------------------------------------------------
  if (window.__MPC_INITIALIZED__) {
    console.warn("[MPC] Script already loaded, skip re-initialization.");
    return;
  }
  window.__MPC_INITIALIZED__ = true;

  // --------------------------------------------------------------------------
  // 工具函数
  // --------------------------------------------------------------------------

  /**
   * 获取 cookie 值
   * @param {string} name
   * @returns {string|null}
   */
  function getCookie(name) {
    var cookies = document.cookie.split("; ");
    var found = cookies.find(function (c) {
      return c.startsWith(name + "=");
    });
    return found
      ? decodeURIComponent(found.split("=").slice(1).join("="))
      : null;
  }

  /**
   * 检查用户是否已登录（通过 MC config cookie）
   * @returns {boolean}
   */
  function isLoggedIn() {
    try {
      var config = window.__MC__CONFIG__;
      if (!config || !config.cookieName) return false;
      var token = getCookie(config.cookieName);
      return !!(token && token.length > 0);
    } catch (e) {
      return false;
    }
  }

  /**
   * 打开登录弹框
   * @param {string} [activityType] - 注册来源
   *
   * 4 层保障机制确保 <login-dialog> web component 能正确接收 activity-type：
   *   1. HTML attribute（标准方式）
   *   2. DOM property（直接赋值）
   *   3. Custom event（让组件监听）
   *   4. Form hidden input（兼容老式表单提交）
   */
  function openLoginDialog(activityType) {
    var loginDialog = document.querySelector("login-dialog");
    if (!loginDialog) {
      console.warn(
        "[MPC] <login-dialog> element not found, cannot open login dialog",
      );
      return;
    }

    if (activityType) {
      // 1. HTML attribute（标准方式）
      try {
        loginDialog.setAttribute("activity-type", activityType);
      } catch (e) {
        /* noop */
      }

      // 2. DOM property（如果组件读取 property 而非 attribute）
      try {
        loginDialog.activityType = activityType;
      } catch (e) {
        /* noop */
      }
      try {
        loginDialog.activitytype = activityType;
      } catch (e) {
        /* noop */
      }

      // 3. Custom event（让组件监听 register-source-changed 事件）
      try {
        loginDialog.dispatchEvent(
          new CustomEvent("register-source-changed", {
            bubbles: true,
            detail: { value: activityType, source: "mpc" },
          }),
        );
      } catch (e) {
        /* noop */
      }

      // 4. 在 dialog 内部插入/更新 hidden input（兜底）
      try {
        var existing = loginDialog.querySelector(
          'input[name="activity_type"], input[name="activityType"]',
        );
        if (existing) {
          existing.value = activityType;
        } else {
          var hidden = document.createElement("input");
          hidden.type = "hidden";
          hidden.name = "activity_type";
          hidden.value = activityType;
          hidden.dataset.mpcInjected = "true";
          loginDialog.appendChild(hidden);
        }
      } catch (e) {
        /* noop */
      }

      console.log("[MPC] openLoginDialog → activityType:", activityType);
    }

    // 打开弹框
    if (typeof loginDialog.open === "function") {
      loginDialog.open();
    } else {
      // 兜底：尝试用 click 触发
      loginDialog.click();
    }
  }

  /**
   * 防抖函数
   * @param {Function} fn
   * @param {number} delay
   * @returns {Function}
   */
  function debounce(fn, delay) {
    var timer = null;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, delay);
    };
  }

  // --------------------------------------------------------------------------
  // API 接口调用（占位，实际路径由接入方确认）
  // --------------------------------------------------------------------------

  /**
   * API 请求基础路径前缀
   * 优先使用 window.__MC__CONFIG__.apiUrl，否则回退到默认域名
   */
  var apiBase =
    (window.__MC__CONFIG__ && window.__MC__CONFIG__.apiUrl) ||
    "https://account.momcuzy.com";

  /**
   * 构造统一请求头：Content-Type + window.__MC__CONFIG__.headers
   * 登录态下追加 Authorization: Bearer <token>（token 从 __MC__CONFIG__.cookieName 指定的 cookie 中读取）
   * @param {Object} [extra] - 额外的请求头
   * @returns {Object}
   */
  function buildApiHeaders(extra) {
    var headers = {
      "Content-Type": "application/json",
    };
    if (window.__MC__CONFIG__ && window.__MC__CONFIG__.headers) {
      Object.keys(window.__MC__CONFIG__.headers).forEach(function (k) {
        headers[k] = window.__MC__CONFIG__.headers[k];
      });
    }
    // 登录态：附加 Bearer token
    try {
      var config = window.__MC__CONFIG__;
      if (config && config.cookieName) {
        var token = getCookie(config.cookieName);
        if (token) headers["Authorization"] = "Bearer " + token;
      }
    } catch (e) {
      /* noop */
    }
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        headers[k] = extra[k];
      });
    }
    return headers;
  }

  /**
   * 获取用户当前总积分
   * 与获取可兑换商品列表共用接口 /api/station/point/getProductsNew
   * 该接口返回的 result.points 字段即为当前用户的总积分
   * @param {string} [caller] - 调用方标识：'init'（页面初始化）、'redeem'（用户点击 Redeem）
   * @returns {Promise<{total: number}>}
   */
  function fetchUserPoints(caller) {
    var headers = buildApiHeaders(caller ? { "X-MPC-Caller": caller } : null);
    return fetch(
      apiBase + "/api/station/point/getProductsNew?productType=7&pageSize=1",
      {
        method: "GET",
        headers: headers,
      },
    )
      .then(function (res) {
        if (!res.ok) throw new Error("fetchUserPoints failed: " + res.status);
        return res.json();
      })
      .then(function (data) {
        var total = 0;
        if (data && data.code === "200" && data.result) {
          total =
            typeof data.result.points === "number" ? data.result.points : 0;
        } else if (data && typeof data.points === "number") {
          // 兼容直接返回 points 在顶层的场景
          total = data.points;
        }
        return { total: total };
      });
  }

  /**
   * 获取积分明细列表（服务端分页）
   * 返回结构：{ code, result: { userPointDetailList: [...], total: N } }
   * 字段映射：remark → 第 1 列（来源），point → 第 2 列（积分变动），createTime → 第 3 列（创建时间）
   * @param {number} [pageNum=1] - 当前页码
   * @param {number} [pageSize=10] - 每页条数
   * @returns {Promise<{list: Array, hasMore: boolean, total: number}>}
   */
  function fetchPointsDetails(pageNum, pageSize) {
    var pn = parseInt(pageNum, 10) || 1;
    var ps = parseInt(pageSize, 10) || 10;
    return fetch(
      apiBase +
        "/api/station/point/getPointDetailPageListNew?pageNum=" +
        pn +
        "&pageSize=" +
        ps,
      {
        method: "GET",
        headers: buildApiHeaders(),
      },
    )
      .then(function (res) {
        if (!res.ok)
          throw new Error("fetchPointsDetails failed: " + res.status);
        return res.json();
      })
      .then(function (data) {
        // 兼容两种返回结构：{ result: { userPointDetailList, total } } 或扁平 { userPointDetailList }
        var result = (data && data.result) || data || {};
        var list = Array.isArray(result.userPointDetailList)
          ? result.userPointDetailList
          : [];
        var total =
          typeof result.total === "number"
            ? result.total
            : data && typeof data.total === "number"
              ? data.total
              : 0;
        // 已知 total 时用 total 判断 hasMore，否则用"返回条数 == pageSize"启发式判断
        var loaded = (pn - 1) * ps + list.length;
        var hasMore = total > 0 ? loaded < total : list.length >= ps;
        return { list: list, hasMore: hasMore, total: total };
      });
  }

  /**
   * 获取兑换记录列表（服务端分页）
   * @param {number} [pageNum=1] - 当前页码
   * @param {number} [pageSize=10] - 每页条数
   * @returns {Promise<{list: Array, hasMore: boolean, total: number}>}
   */
  function fetchRedemptionRecords(pageNum, pageSize) {
    var pn = parseInt(pageNum, 10) || 1;
    var ps = parseInt(pageSize, 10) || 5;
    return fetch(
      apiBase +
        "/api/station/point/exchangeProductHistory?pageNum=" +
        pn +
        "&pageSize=" +
        ps,
      {
        method: "GET",
        headers: buildApiHeaders(),
      },
    )
      .then(function (res) {
        if (!res.ok)
          throw new Error("fetchRedemptionRecords failed: " + res.status);
        return res.json();
      })
      .then(function (data) {
        // 返回结构：{ code, result: { exchangeProductList: [...], total: N } }
        var result = (data && data.result) || {};
        var list = Array.isArray(result.exchangeProductList)
          ? result.exchangeProductList
          : [];
        var total = typeof result.total === "number" ? result.total : 0;
        // total 已知：当前累计条数 = (pageNum-1) * pageSize + list.length，与 total 比较判断 hasMore
        var loaded = (pn - 1) * ps + list.length;
        var hasMore = total > 0 ? loaded < total : list.length >= ps;
        return { list: list, hasMore: hasMore, total: total };
      });
  }

  /**
   * 批量获取兑换产品数据
   * @returns {Promise<Array<{id: string, title: string, imageUrl: string, points: number, stock: number, redeemLimitReached: boolean}>>}
   * 商品类型过滤，不传默认 [3]（优惠券）。3=优惠券 4=礼品卡 5=100:1券 6=免运费券 7=实物
   */
  function fetchRedeemProducts() {
    return fetch(
      apiBase + "/api/station/point/getProductsNew?productType=7&pageSize=1000",
      {
        method: "GET",
        headers: buildApiHeaders(),
      },
    ).then(function (res) {
      if (!res.ok) throw new Error("fetchRedeemProducts failed: " + res.status);
      return res.json();
    });
  }

  /**
   * 执行积分兑换
   * POST /api/station/point/exchangeProductNew
   * @param {string} productCode - 商品编码（后台 productCode 字段）
   * @param {number} [number=1] - 兑换数量，默认 1
   * @param {number} usedPoints - 消耗的积分（=卡片的 exchangePoint）
   * @returns {Promise<{code, result, message?}>}
   */
  function doRedeem(productCode, number, usedPoints) {
    return fetch(apiBase + "/api/station/point/exchangeProductNew", {
      method: "POST",
      headers: buildApiHeaders(),
      body: JSON.stringify({
        productCode: productCode,
        number: typeof number === "number" && number > 0 ? number : 1,
        usedPoints: typeof usedPoints === "number" ? usedPoints : 0,
      }),
    }).then(function (res) {
      return res.json().then(function (data) {
        // HTTP 层面错误（4xx/5xx）
        if (!res.ok) {
          var httpErr = new Error(data.message || "Redeem failed");
          httpErr.serverMsg = data.message;
          httpErr.serverCode = data.code;
          throw httpErr;
        }
        // 业务层面错误：code !== '200' 即视为兑换失败
        // 业务错误码映射由调用方在 catch 中通过 redeemErrorMessages 查找
        if (String(data.code) !== "200") {
          var bizErr = new Error(data.message || "Redeem failed");
          bizErr.serverMsg = data.message;
          bizErr.serverCode = data.code;
          throw bizErr;
        }
        return data;
      });
    });
  }

  // --------------------------------------------------------------------------
  // 主逻辑：每个 section 独立初始化
  // --------------------------------------------------------------------------

  /**
   * 检测登录来源标记：登录/注册成功刷新后，自动滚动到该组件
   * - 使用 sessionStorage（标签页级，关闭即失效，避免跨标签污染）
   * - 5 分钟有效期：超时则忽略（防止用户取消登录后导航导致误滚）
   * - **关键：必须同时检测到已登录状态**（防止用户打开弹框后刷新页面导致误滚）
   * - 滚动后立即清理标记（避免重复滚动）
   * - 等待 300ms 确保页面布局稳定、字体加载、懒加载图片到位
   * @param {HTMLElement} sectionEl - 当前 section 元素
   * @param {string} sectionId - 当前 section ID
   */
  function scrollToSectionIfFromLogin(sectionEl, sectionId) {
    try {
      var raw = sessionStorage.getItem("mpc_login_source");
      if (!raw) return;

      var data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        sessionStorage.removeItem("mpc_login_source");
        return;
      }

      // 校验：sectionId 匹配 + 5 分钟内
      var FIVE_MIN = 5 * 60 * 1000;
      if (
        !data ||
        data.sectionId !== sectionId ||
        Date.now() - (data.ts || 0) >= FIVE_MIN
      ) {
        sessionStorage.removeItem("mpc_login_source");
        return;
      }

      // **关键检查：只有用户真的已登录才滚动**
      // 防止"打开弹框 → 刷新页面"等未登录场景误滚
      if (!isLoggedIn()) {
        console.log(
          "[MPC] 检测到登录来源标记，但用户仍未登录（可能仅刷新页面），清除标记",
        );
        sessionStorage.removeItem("mpc_login_source");
        return;
      }

      console.log("[MPC] 检测到登录成功 + 标记匹配，300ms 后将滚动到该组件");
      // 清理标记（无论后续是否成功滚动）
      sessionStorage.removeItem("mpc_login_source");
      // 延迟滚动：等页面布局/字体/懒加载图片稳定
      setTimeout(function () {
        try {
          sectionEl.scrollIntoView({ behavior: "smooth", block: "start" });
          // 高亮一下 banner，让用户明确知道这就是刚登录的组件
          sectionEl.classList.add("mpc-section--just-logged-in");
          setTimeout(function () {
            sectionEl.classList.remove("mpc-section--just-logged-in");
          }, 2000);
        } catch (e) {
          /* noop */
        }
      }, 300);
    } catch (e) {
      /* noop */
    }
  }

  /**
   * 通用分页器渲染
   * @param {HTMLElement} container - 分页器容器
   * @param {number} total - 总条数
   * @param {number} page - 当前页（1-based）
   * @param {number} pageSize - 每页条数
   * @param {function} onPageChange - 页码切换回调
   */
  function renderPagination(container, total, page, pageSize, onPageChange) {
    if (!container) return;
    var totalPages = Math.max(1, Math.ceil(total / pageSize));
    // 只有超过 1 页才显示分页器
    if (totalPages <= 1) {
      container.style.display = "none";
      container.innerHTML = "";
      return;
    }
    container.style.display = "";
    container.innerHTML = "";

    // 上一页按钮
    var prevBtn = document.createElement("button");
    prevBtn.type = "button";
    prevBtn.className = "mpc-pagination__arrow mpc-pagination__arrow--prev";
    prevBtn.disabled = page <= 1;
    prevBtn.setAttribute("aria-label", "Previous page");
    prevBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><polyline points="15 6 9 12 15 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    prevBtn.addEventListener("click", function () {
      if (page > 1) onPageChange(page - 1);
    });
    container.appendChild(prevBtn);

    // 页码列表
    var pageList = document.createElement("div");
    pageList.className = "mpc-pagination__pages";
    var pages = computeVisiblePages(page, totalPages);
    pages.forEach(function (p) {
      if (p === "...") {
        var ellipsis = document.createElement("span");
        ellipsis.className = "mpc-pagination__ellipsis";
        ellipsis.textContent = "…";
        pageList.appendChild(ellipsis);
      } else {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className =
          "mpc-pagination__page" + (p === page ? " is-active" : "");
        btn.textContent = p;
        btn.setAttribute("aria-label", "Page " + p);
        if (p === page) btn.setAttribute("aria-current", "page");
        btn.addEventListener("click", function () {
          onPageChange(p);
        });
        pageList.appendChild(btn);
      }
    });
    container.appendChild(pageList);

    // 下一页按钮
    var nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = "mpc-pagination__arrow mpc-pagination__arrow--next";
    nextBtn.disabled = page >= totalPages;
    nextBtn.setAttribute("aria-label", "Next page");
    nextBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><polyline points="9 6 15 12 9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    nextBtn.addEventListener("click", function () {
      if (page < totalPages) onPageChange(page + 1);
    });
    container.appendChild(nextBtn);
  }

  /**
   * 计算可见页码（首尾必显，中间按需省略）
   *   1 … 4 5 [6] 7 8 … 20
   */
  function computeVisiblePages(current, total) {
    if (total <= 7) {
      var arr = [];
      for (var i = 1; i <= total; i++) arr.push(i);
      return arr;
    }
    var pages = [1];
    var left = Math.max(2, current - 1);
    var right = Math.min(total - 1, current + 1);
    if (left > 2) pages.push("...");
    for (var j = left; j <= right; j++) pages.push(j);
    if (right < total - 1) pages.push("...");
    pages.push(total);
    return pages;
  }

  /**
   * 初始化单个 member-promotion-cozy section
   * @param {HTMLElement} sectionEl
   */
  function initSection(sectionEl) {
    var sectionId = sectionEl.dataset.sectionId;
    var config =
      (window.__MPC_CONFIG__ && window.__MPC_CONFIG__[sectionId]) || {};

    // 配置项读取
    var registerSource = config.registerSource || "BDMemberCozycoinsEvent";
    var cardRedeemLabel = config.cardRedeemLabel || "Redeem";
    var cardOutOfStockLabel = config.cardOutOfStockLabel || "Out of Stock";
    var loadMoreRows = config.loadMoreRows || { pc: 2, mobile: 3 };
    var toastSuccessContent =
      config.toastSuccessContent ||
      "Redeemed successfully! Check it in Account > Coupons.";
    var insufficientMsgTpl =
      config.insufficientMsgTpl ||
      "Insufficient CozyCoins. You need {need} but only have {have}.";
    var confirmBtnLabel = config.confirmBtnLabel || "Confirm";

    // 兑换失败错误码 → 提示文案映射
    // 优先级：config.redeemErrorMessages（section schema 可编辑） > 默认英文文案
    var defaultRedeemErrorMessages = {
      10004251: "Redeeming, please do not submit again.",
      10004252: "Product does not exist.",
      10004253: "This product is currently unavailable for redemption.",
      10004254: "Product has been taken offline.",
      10004255: "Insufficient membership level, please upgrade first.",
      10004256: "Insufficient CozyCoins.",
      10004257: "Out of stock.",
      10004258: "You've reached the redemption limit for this product.",
      10004259: "Redemption failed, please try again later.",
      10004506: "Please bind your email first.",
      10004512:
        "Limit: 1 per day, max 3 per month (category restriction 1).",
      10004513:
        "Limit: 1 per day, max 3 per month (category restriction 2).",
    };
    // config.redeemErrorMessages 在 section schema 里是 JSON 字符串
    // （textarea 让商家在 theme editor 里编辑 JSON 配置）
    // 解析失败时安全降级到默认配置
    var userRedeemErrorMessages = {};
    try {
      if (typeof config.redeemErrorMessages === "string") {
        var parsed = JSON.parse(config.redeemErrorMessages);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          userRedeemErrorMessages = parsed;
        }
      } else if (
        config.redeemErrorMessages &&
        typeof config.redeemErrorMessages === "object" &&
        !Array.isArray(config.redeemErrorMessages)
      ) {
        // 也支持直接传对象（外部 JS 调用 initSection 时）
        userRedeemErrorMessages = config.redeemErrorMessages;
      }
    } catch (e) {
      console.warn("[MPC] Invalid redeemErrorMessages JSON, using defaults:", e);
    }
    var redeemErrorMessages = Object.assign(
      {},
      defaultRedeemErrorMessages,
      userRedeemErrorMessages,
    );

    // 设备检测（需要在分页默认 pageSize 之前确定）
    var isMobile = window.innerWidth <= 768;

    // 分页配置
    // 运行时默认值由 JS 按设备决定（PC=5 / Mobile=10）。
    // 商家主动改过 schema 里的 page_size 时，使用商家配置；未改过则按设备兜底。
    // - redemptionPageSizeSet（Liquid 注入）：true = 商家改过；false = 仍是 schema 默认值
    var defaultPointsPageSize = 10;
    var defaultRedemptionPageSize = isMobile ? 10 : 5;
    var pointsPageSize = config.pointsPageSizeSet
      ? parseInt(config.pointsPageSize, 10) || defaultPointsPageSize
      : defaultPointsPageSize;
    var redemptionPageSize = config.redemptionPageSizeSet
      ? parseInt(config.redemptionPageSize, 10) || defaultRedemptionPageSize
      : defaultRedemptionPageSize;

    // 检测登录来源标记：登录/注册成功刷新后，自动滚回该组件
    scrollToSectionIfFromLogin(sectionEl, sectionId);

    // 产品 ID 列表
    var productIdsRaw = (sectionEl.dataset.productIds || "").trim();
    var productIds = productIdsRaw
      ? productIdsRaw
          .split(",")
          .map(function (s) {
            return s.trim();
          })
          .filter(Boolean)
      : [];

    // DOM 引用
    var bannerUnloggedEl = sectionEl.querySelector("[data-mpc-unlogged]");
    var bannerLoggedEl = sectionEl.querySelector("[data-mpc-logged]");
    var pointsValueEl = sectionEl.querySelector("[data-mpc-points-value]");
    var gridEl = sectionEl.querySelector("[data-mpc-grid]");
    var loadMoreWrapEl = sectionEl.querySelector("[data-mpc-load-more]");
    var loadMoreBtnEl = sectionEl.querySelector("[data-mpc-load-more-btn]");

    // 弹框元素
    var rulesDialogEl = document.getElementById("rules-dialog-" + sectionId);
    var cozyCoinsDlgEl = document.getElementById(
      "cozycoins-dialog-" + sectionId,
    );
    var redemptionDlgEl = document.getElementById(
      "redemption-dialog-" + sectionId,
    );
    var insufficientDlgEl = document.getElementById(
      "insufficient-dialog-" + sectionId,
    );
    var confirmDlgEl = document.getElementById("confirm-dialog-" + sectionId);

    // 缓存 section 注入的 st.confirm_rich_text 兜底内容（仅在初始化时读一次，
    // 之后即便弹框内 .mpc-confirm__rich-text 被覆盖也能从 fallback 还原）
    var confirmRichTextFallback = "";
    if (confirmDlgEl) {
      var initialRichEl = confirmDlgEl.querySelector(
        "[data-mpc-confirm-rich-text]",
      );
      if (initialRichEl) {
        confirmRichTextFallback = initialRichEl.innerHTML || "";
      }
    }

    // Toast
    var toastEl = document.querySelector("[data-mpc-toast]");

    // 状态
    var userTotalPoints = 0;
    var currentRedeemProductCode = null;
    var currentRedeemProductPoints = 0;
    var allCards = []; // {el, productId, apiData}
    var isLoadMoreVisible = false;

    // --------------------------------------------------------------------------
    // 弹框管理
    // --------------------------------------------------------------------------

    /**
     * 获取弹框内所有可聚焦元素（用于焦点陷阱）
     * @param {HTMLElement} dialogEl
     * @returns {HTMLElement[]}
     */
    function getFocusableElements(dialogEl) {
      if (!dialogEl) return [];
      // 排除 aria-hidden=true / display:none 的元素
      var selector =
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      var nodes = dialogEl.querySelectorAll(selector);
      var result = [];
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (node.offsetWidth === 0 && node.offsetHeight === 0) continue;
        if (node.getAttribute("aria-hidden") === "true") continue;
        result.push(node);
      }
      return result;
    }

    /**
     * 在弹框打开时安装焦点陷阱：Tab/Shift+Tab 在第一个/最后一个可聚焦元素间循环，
     * 关闭弹框时还原焦点到触发元素
     * @param {HTMLElement} dialogEl
     * @param {HTMLElement} [triggerEl] - 打开弹框的触发元素（用于关闭后还原焦点）
     */
    function installDialogFocusTrap(dialogEl, triggerEl) {
      if (!dialogEl) return function () {};

      // 记录触发元素，便于关闭时恢复焦点
      if (triggerEl) {
        try {
          dialogEl.__mpcTriggerEl = triggerEl;
        } catch (e) {
          /* noop */
        }
      }

      var onKeyDown = function (e) {
        if (e.key !== "Tab") return;
        var focusable = getFocusableElements(dialogEl);
        if (!focusable.length) {
          // 弹框内没有可聚焦元素时，把焦点锁在 dialog 本身
          e.preventDefault();
          dialogEl.focus();
          return;
        }
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        var active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !dialogEl.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !dialogEl.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      dialogEl.addEventListener("keydown", onKeyDown);

      // 初始聚焦：rAF 等 display:flex 生效后再聚焦第一个可聚焦元素
      var initialFocusTimer = requestAnimationFrame(function () {
        var focusable = getFocusableElements(dialogEl);
        var target = focusable[0] || dialogEl;
        // 关闭按钮优先（如果存在），更符合用户对弹框的预期
        var closeBtn = dialogEl.querySelector(
          ".mpc-dialog__close, [data-mpc-close]:first-of-type",
        );
        if (closeBtn && focusable.indexOf(closeBtn) !== -1) {
          target = closeBtn;
        }
        try {
          target.focus();
        } catch (e) {
          /* noop */
        }
      });

      // 返回拆除函数
      return function uninstall() {
        cancelAnimationFrame(initialFocusTimer);
        dialogEl.removeEventListener("keydown", onKeyDown);
        // 恢复焦点到触发元素
        var trig = dialogEl.__mpcTriggerEl;
        if (trig && document.contains(trig)) {
          try {
            trig.focus();
          } catch (e) {
            /* noop */
          }
        }
        dialogEl.__mpcTriggerEl = null;
      };
    }

    /**
     * 打开弹框
     * @param {HTMLElement} dialogEl
     * @param {HTMLElement} [triggerEl] - 触发弹框打开的元素（用于关闭后还原焦点）
     */
    function openDialog(dialogEl, triggerEl) {
      if (!dialogEl) return;
      // 清理上一次可能残留的 focus 拆除器
      if (typeof dialogEl.__mpcUninstallFocus === "function") {
        dialogEl.__mpcUninstallFocus();
        dialogEl.__mpcUninstallFocus = null;
      }
      dialogEl.style.display = "flex";
      // 用 rAF 确保 display 已生效后添加 class 触发动画
      requestAnimationFrame(function () {
        dialogEl.classList.add("is-open");
      });
      document.body.style.overflow = "hidden";

      // 安装焦点陷阱（Tab/Shift+Tab 循环 + 初始聚焦 + 关闭时恢复）
      dialogEl.__mpcUninstallFocus = installDialogFocusTrap(
        dialogEl,
        triggerEl,
      );

      // ESC 关闭
      function onKeyDown(e) {
        if (e.key === "Escape") {
          closeDialog(dialogEl);
          document.removeEventListener("keydown", onKeyDown);
        }
      }
      document.addEventListener("keydown", onKeyDown);

      // 点击遮罩关闭（点到 overlay 本身时）
      // - 桌面端（≥769px）: config.enableOverlayClose（默认 false）
      // - 移动端（≤768px）: config.enableOverlayCloseMobile（默认 true，符合移动端用户习惯）
      var isMobile = window.innerWidth <= 768;
      var allowOverlayClose = isMobile
        ? !!config.enableOverlayCloseMobile
        : !!config.enableOverlayClose;

      if (allowOverlayClose) {
        function onOverlayClick(e) {
          if (e.target === dialogEl) {
            closeDialog(dialogEl);
            dialogEl.removeEventListener("click", onOverlayClick);
          }
        }
        dialogEl.addEventListener("click", onOverlayClick);
      }
    }

    /**
     * 关闭弹框
     * @param {HTMLElement} dialogEl
     */
    function closeDialog(dialogEl) {
      if (!dialogEl) return;
      dialogEl.classList.remove("is-open");
      dialogEl.style.display = "none";
      // 拆除焦点陷阱，并恢复焦点到触发元素
      if (typeof dialogEl.__mpcUninstallFocus === "function") {
        dialogEl.__mpcUninstallFocus();
        dialogEl.__mpcUninstallFocus = null;
      }
      // 检查是否还有其他弹框打开
      var openDialogs = document.querySelectorAll(".mpc-overlay.is-open");
      if (!openDialogs.length) {
        document.body.style.overflow = "";
      }
    }

    // 所有弹框的关闭按钮绑定（支持每个弹框内多个 [data-mpc-close]，如 X 关闭 + Get it）
    [
      rulesDialogEl,
      cozyCoinsDlgEl,
      redemptionDlgEl,
      insufficientDlgEl,
      confirmDlgEl,
    ].forEach(function (dlg) {
      if (!dlg) return;
      var closeBtns = dlg.querySelectorAll("[data-mpc-close]");
      closeBtns.forEach(function (closeBtn) {
        closeBtn.addEventListener("click", function () {
          closeDialog(dlg);
        });
      });
    });

    // --------------------------------------------------------------------------
    // Toast
    // --------------------------------------------------------------------------

    var toastTimer = null;

    /**
     * 显示 Toast
     * @param {string} msg - 内容文字（可选，不传则用默认）
     * @param {boolean} [showIcon=true] - 是否显示图标
     */
    function showToast(msg, showIcon) {
      if (!toastEl) return;
      var contentEl = toastEl.querySelector("[data-mpc-toast-content]");
      var iconEl = toastEl.querySelector("[data-mpc-toast-icon]");
      if (contentEl) contentEl.textContent = msg || toastSuccessContent;
      if (iconEl) {
        iconEl.style.display = showIcon === false ? "none" : "";
      }
      toastEl.classList.add("is-visible");
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        toastEl.classList.remove("is-visible");
      }, 4000);
    }

    // Toast 关闭按钮
    if (toastEl) {
      var toastCloseBtn = toastEl.querySelector("[data-mpc-toast-close]");
      if (toastCloseBtn) {
        toastCloseBtn.addEventListener("click", function () {
          clearTimeout(toastTimer);
          toastEl.classList.remove("is-visible");
        });
      }
    }

    // --------------------------------------------------------------------------
    // Banner：登录态切换
    // --------------------------------------------------------------------------

    function showLoggedBanner() {
      if (bannerUnloggedEl) bannerUnloggedEl.style.display = "none";
      if (bannerLoggedEl) bannerLoggedEl.style.display = "";
    }

    function showUnloggedBanner() {
      if (bannerUnloggedEl) bannerUnloggedEl.style.display = "";
      if (bannerLoggedEl) bannerLoggedEl.style.display = "none";
    }

    // --------------------------------------------------------------------------
    // 获取用户积分
    // --------------------------------------------------------------------------

    function loadUserPoints() {
      return fetchUserPoints("init")
        .then(function (data) {
          userTotalPoints =
            data && typeof data.total === "number" ? data.total : 0;
          // 更新 banner 积分显示
          if (pointsValueEl)
            pointsValueEl.textContent = userTotalPoints.toLocaleString();
          // 更新 CozyCoins Details 弹框标题积分
          var dlgPointsEl = document.querySelector(
            "#cozycoins-dialog-" + sectionId + " [data-mpc-dialog-points]",
          );
          if (dlgPointsEl)
            dlgPointsEl.textContent = userTotalPoints.toLocaleString();
          // 更新所有卡片按钮状态
          refreshCardButtonStates();
          return data;
        })
        .catch(function (err) {
          console.warn("[MPC] Failed to load user points:", err);
        });
    }

    // --------------------------------------------------------------------------
    // 登录状态初始化
    // --------------------------------------------------------------------------

    function initLoginState() {
      if (isLoggedIn()) {
        showLoggedBanner();
        loadUserPoints();
      } else {
        showUnloggedBanner();
      }
    }

    // 监听登录成功事件
    // 防重入：momcozy-login.es.js 在登录态下，每个 <login-dialog> 挂载时都会自动派发一次 LUTE:AUTH:LOGIN
    // （detail.type === "keep"），init 期间若页面有多个 dialog 会重复触发。
    // 真实用户登录/注册事件 detail.type === "assembly-login"（或无 type），需正常刷新积分。
    document.addEventListener("LUTE:AUTH:LOGIN", function (e) {
      showLoggedBanner();
      // 跳过 dialog 启动时自动派发的 "keep" 事件（积分已在 initLoginState 中加载过）
      if (e && e.detail && e.detail.type === "keep") return;
      loadUserPoints();
    });

    // 未登录按钮点击
    sectionEl
      .querySelectorAll("[data-mpc-login-trigger]")
      .forEach(function (btn) {
        btn.addEventListener("click", function () {
          // 标记登录来源：登录/注册成功刷新后，自动滚回该组件
          try {
            sessionStorage.setItem(
              "mpc_login_source",
              JSON.stringify({
                sectionId: sectionId,
                ts: Date.now(),
              }),
            );
          } catch (e) {
            /* noop */
          }
          openLoginDialog(registerSource);
        });
      });

    // Banner 已登录操作按钮
    var cozyCoinsDetailsBtn = sectionEl.querySelector(
      '[data-mpc-action="cozycoins-details"]',
    );
    var redemptionRecordBtn = sectionEl.querySelector(
      '[data-mpc-action="redemption-record"]',
    );

    if (cozyCoinsDetailsBtn) {
      cozyCoinsDetailsBtn.addEventListener("click", function () {
        openCozyCoinDetails(cozyCoinsDetailsBtn);
      });
    }

    if (redemptionRecordBtn) {
      redemptionRecordBtn.addEventListener("click", function () {
        openRedemptionRecord(redemptionRecordBtn);
      });
    }

    // --------------------------------------------------------------------------
    // 规则弹框触发
    // --------------------------------------------------------------------------

    var rulesTrigger = sectionEl.querySelector(
      '[data-mpc-open="rules-dialog-' + sectionId + '"]',
    );
    if (rulesTrigger) {
      rulesTrigger.addEventListener("click", function () {
        openDialog(rulesDialogEl, rulesTrigger);
      });
    }

    // --------------------------------------------------------------------------
    // CozyCoins Details 弹框
    // --------------------------------------------------------------------------

    // 分页状态：每个 section 独立维护
    // 积分明细：服务端分页
    // - PC 端：页码切换，每页替换 + 骨架屏 + 分页器
    // - Mobile 端：触底加载（无限滚动），每页追加 + 底部 loading 指示器，初始化时显示骨架屏
    var pointsPagination = {
      page: 1,
      pageSize: pointsPageSize,
      total: 0,
      hasMore: true,
      list: [],
      isLoading: false,
      scrollHandler: null,
    };
    // 兑换记录：服务端分页
    // - PC 端：页码切换，每页替换 + 骨架屏 + 分页器
    // - Mobile 端：触底加载（无限滚动），每页追加 + 底部 loading 指示器，初始化时显示骨架屏
    var redemptionPagination = {
      page: 1,
      // 兜底用 redemptionPageSize（在前面按 isMobile 计算过：PC=5 / Mobile=10）
      pageSize: redemptionPageSize,
      total: 0,
      hasMore: true,
      list: [],
      isLoading: false,
      scrollHandler: null,
    };

    /**
     * 设置移动端 loading 指示器的显示状态
     *  - "loading"  : 正在加载（spinner 可见）
     *  - "reserved" : 保留高度但 spinner 不可见（list 已有数据，未在加载）
     *  - "hidden"   : 完全隐藏（list 为空或首屏初始化前）
     * 三种状态都通过 visibility + display 控制，避免弹框高度抖动
     */
    function setLoadMoreState(el, state) {
      if (!el) return;
      if (state === "loading") {
        el.style.display = "";
        el.classList.remove("mpc-load-more--reserved");
        // 屏幕阅读器通知：正在加载
        el.setAttribute("aria-busy", "true");
        el.removeAttribute("aria-hidden");
      } else if (state === "reserved") {
        el.style.display = "";
        el.classList.add("mpc-load-more--reserved");
        // 占位但 SR 不应朗读（用 aria-hidden 隐藏，但保留 layout 高度）
        el.setAttribute("aria-busy", "false");
        el.setAttribute("aria-hidden", "true");
      } else {
        el.style.display = "none";
        el.classList.remove("mpc-load-more--reserved");
        el.setAttribute("aria-busy", "false");
        el.setAttribute("aria-hidden", "true");
      }
    }

    function openCozyCoinDetails(triggerEl) {
      // 先清空旧数据 + 显示骨架屏
      var listEl =
        cozyCoinsDlgEl &&
        cozyCoinsDlgEl.querySelector("[data-mpc-points-list]");
      var emptyEl =
        cozyCoinsDlgEl &&
        cozyCoinsDlgEl.querySelector("[data-mpc-points-empty]");
      var skeletonEl =
        cozyCoinsDlgEl &&
        cozyCoinsDlgEl.querySelector("[data-mpc-points-skeleton]");
      var pagerEl =
        cozyCoinsDlgEl &&
        cozyCoinsDlgEl.querySelector("[data-mpc-points-pagination]");
      var loadMoreEl =
        cozyCoinsDlgEl &&
        cozyCoinsDlgEl.querySelector("[data-mpc-points-load-more]");
      if (listEl) listEl.innerHTML = "";
      if (emptyEl) emptyEl.style.display = "none";
      if (pagerEl) pagerEl.style.display = "none";
      if (loadMoreEl) setLoadMoreState(loadMoreEl, "hidden");
      if (skeletonEl) skeletonEl.style.display = "";

      openDialog(cozyCoinsDlgEl, triggerEl);

      // 重置分页状态，请求第 1 页（initial=true → 显示骨架屏）
      pointsPagination.page = 1;
      pointsPagination.total = 0;
      pointsPagination.hasMore = true;
      pointsPagination.list = [];
      pointsPagination.isLoading = false;
      // 先解绑旧监听（防止 dialog 关闭后再次打开时残留）
      if (listEl) detachPointsScrollObserver(listEl);
      loadPointsPage(1, listEl, emptyEl, skeletonEl, pagerEl, loadMoreEl, true);
    }

    /**
     * 拉取指定页的积分明细
     * - isInitial=true: 初始化 / PC 端翻页 → 显示骨架屏 + 替换列表
     * - isInitial=false: Mobile 端触底加载 → 显示 loading 指示器 + 追加到列表
     * - 错误处理：初始化失败显示空态；触底加载失败仅隐藏 loading 指示器
     */
    function loadPointsPage(
      pageNum,
      listEl,
      emptyEl,
      skeletonEl,
      pagerEl,
      loadMoreEl,
      isInitial,
    ) {
      if (!listEl) return;
      if (pointsPagination.isLoading) return;
      var mobile = isMobile;
      var isMobileLoadMore = mobile && !isInitial;
      pointsPagination.isLoading = true;

      if (isMobileLoadMore) {
        // 触底加载：显示 loading 指示器，保留已有 list
        if (loadMoreEl) setLoadMoreState(loadMoreEl, "loading");
        if (emptyEl) emptyEl.style.display = "none";
      } else {
        // 初始化或 PC 翻页：显示骨架屏，清空 list
        if (skeletonEl) skeletonEl.style.display = "";
        if (emptyEl) emptyEl.style.display = "none";
        if (pagerEl) pagerEl.style.display = "none";
        if (loadMoreEl) setLoadMoreState(loadMoreEl, "hidden");
        listEl.innerHTML = "";
      }

      pointsPagination.page = pageNum;

      fetchPointsDetails(pageNum, pointsPagination.pageSize)
        .then(function (data) {
          pointsPagination.isLoading = false;
          if (skeletonEl) skeletonEl.style.display = "none";

          var newList = (data && data.list) || [];
          if (typeof data.total === "number") {
            pointsPagination.total = data.total;
          }
          pointsPagination.hasMore = !!data.hasMore;

          // 移动端有数据时：保留 loading 高度（避免弹框抖动）；无数据或 PC 端：完全隐藏
          var hasData = newList.length > 0;
          if (loadMoreEl) {
            if (mobile && hasData) {
              setLoadMoreState(loadMoreEl, "reserved");
            } else {
              setLoadMoreState(loadMoreEl, "hidden");
            }
          }

          if (isMobileLoadMore) {
            // 移动端触底加载：追加到已有列表
            pointsPagination.list = pointsPagination.list.concat(newList);
            appendPointsRows(listEl, newList);
            // 列表不满一屏且还有更多数据：自动加载下一批，避免"还有数据但用户看不到"
            if (
              pointsPagination.hasMore &&
              listEl.scrollHeight <= listEl.clientHeight
            ) {
              setTimeout(function () {
                loadPointsPage(
                  pageNum + 1,
                  listEl,
                  emptyEl,
                  skeletonEl,
                  pagerEl,
                  loadMoreEl,
                  false,
                );
              }, 50);
            }
            return;
          }

          // 初始化 / PC 翻页：替换列表
          pointsPagination.list = newList;
          if (!newList.length) {
            if (pageNum === 1 && emptyEl) emptyEl.style.display = "";
            // 即便空数据，分页器可能仍需展示
            renderPointsPage(listEl, pagerEl);
            return;
          }
          renderPointsPage(listEl, pagerEl);
          // 移动端初始化后绑定滚动监听
          if (mobile) {
            attachPointsScrollObserver(
              listEl,
              loadMoreEl,
              emptyEl,
              skeletonEl,
              pagerEl,
            );
          }
        })
        .catch(function (err) {
          pointsPagination.isLoading = false;
          console.warn("[MPC] Failed to load points details:", err);
          if (skeletonEl) skeletonEl.style.display = "none";
          // 失败时：list 已有数据就保留高度，否则隐藏
          var pointsListHasData =
            listEl && listEl.children && listEl.children.length > 0;
          if (loadMoreEl) {
            if (mobile && pointsListHasData) {
              setLoadMoreState(loadMoreEl, "reserved");
            } else {
              setLoadMoreState(loadMoreEl, "hidden");
            }
          }
          if (isInitial) {
            if (listEl) listEl.innerHTML = "";
            if (emptyEl) emptyEl.style.display = "";
          }
        });
    }

    /**
     * 移动端：给列表绑定滚动监听，触底时自动加载下一页
     * 性能优化：rAF 包裹避免每次 scroll 事件都做 DOM 测量，
     * 防止高频 scroll 触发 layout thrash
     */
    function attachPointsScrollObserver(
      listEl,
      loadMoreEl,
      emptyEl,
      skeletonEl,
      pagerEl,
    ) {
      // 防止重复绑定
      if (pointsPagination.scrollHandler) {
        listEl.removeEventListener("scroll", pointsPagination.scrollHandler);
        pointsPagination.scrollHandler = null;
      }
      var ticking = false;
      var handler = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          // 防止 resize 到 PC 后误触发：仅 mobile 模式使用滚动加载
          if (!isMobile) return;
          if (pointsPagination.isLoading || !pointsPagination.hasMore) return;
          // 触底检测：距离底部 < 50px 视为触底
          if (
            listEl.scrollTop + listEl.clientHeight >=
            listEl.scrollHeight - 50
          ) {
            loadPointsPage(
              pointsPagination.page + 1,
              listEl,
              emptyEl,
              skeletonEl,
              pagerEl,
              loadMoreEl,
              false,
            );
          }
        });
      };
      pointsPagination.scrollHandler = handler;
      listEl.addEventListener("scroll", handler, { passive: true });
    }

    /**
     * 移动端：解绑滚动监听
     */
    function detachPointsScrollObserver(listEl) {
      if (pointsPagination.scrollHandler) {
        listEl.removeEventListener("scroll", pointsPagination.scrollHandler);
        pointsPagination.scrollHandler = null;
      }
    }

    /**
     * 移动端：追加积分明细行到列表（不重建整个列表）
     */
    function appendPointsRows(listEl, items) {
      if (!listEl || !Array.isArray(items) || !items.length) return;
      var frag = document.createDocumentFragment();
      items.forEach(function (item) {
        var row = document.createElement("div");
        row.className = "mpc-points-row";
        var source = escapeHtml(String(item.remark || ""));
        // 字段：point → 第 2 列（积分变动）
        // - 已有 "-" 前缀（负数）：原样使用
        // - 否则（正数/纯数值）：在前面补 "+" 以视觉区分正负；空值保持空
        var point = String(item.point == null ? "" : item.point);
        var displayPoint =
          point.charAt(0) === "-" ? point : point ? "+" + point : point;
        var points = escapeHtml(displayPoint);
        //mobile端使用创建时间，pc端使用过期时间
        var createdAt = escapeHtml(formatMarketTime(isMobile ? item.createTime : item.expiredTime));
        row.innerHTML =
          '<span class="mpc-points-row__source">' +
          source +
          "</span>" +
          '<span class="mpc-points-row__points points-val">' +
          points +
          "</span>" +
          '<span class="mpc-points-row__date">' +
          createdAt +
          "</span>";
        frag.appendChild(row);
      });
      listEl.appendChild(frag);
    }

    /**
     * 渲染积分明细当前页
     * - PC 端：替换整个列表 + 渲染分页器
     * - Mobile 端（仅 initial=true 时调用）：渲染整页 + 触发滚动监听
     * 数据已由 loadPointsPage 写入 pointsPagination.list。
     * 桌面端：3 列（Source / Points / Expiry Date）
     * 移动端：左列（日期 + 来源），右列（积分）
     * 字段映射：remark → 第 1 列（来源），point → 第 2 列（积分变动），createTime → 第 3 列（创建时间）
     */
    function renderPointsPage(listEl, pagerEl) {
      var list = pointsPagination.list || [];
      var page = pointsPagination.page;
      var pageSize = pointsPagination.pageSize;
      // total 用于分页器；有 total 时用 total，否则用 "已加载的页数 * pageSize" 作为兜底
      var total =
        pointsPagination.total ||
        page * pageSize + (pointsPagination.hasMore ? 1 : 0);

      listEl.innerHTML = "";
      appendPointsRows(listEl, list);
      renderPagination(
        pagerEl,
        total,
        page,
        pageSize,
        function (newPage) {
          // 切换页码（仅 PC）→ 显示骨架屏 → 服务端拉取 → 渲染
          loadPointsPage(
            newPage,
            listEl,
            null,
            null,
            pagerEl,
            null,
            true,
          );
        },
      );
    }

    // --------------------------------------------------------------------------
    // Redemption Record 弹框
    // --------------------------------------------------------------------------

    function openRedemptionRecord(triggerEl) {
      // 先清空旧数据 + 显示骨架屏
      var listEl =
        redemptionDlgEl &&
        redemptionDlgEl.querySelector("[data-mpc-redemption-list]");
      var emptyEl =
        redemptionDlgEl &&
        redemptionDlgEl.querySelector("[data-mpc-redemption-empty]");
      var skeletonEl =
        redemptionDlgEl &&
        redemptionDlgEl.querySelector("[data-mpc-redemption-skeleton]");
      var pagerEl =
        redemptionDlgEl &&
        redemptionDlgEl.querySelector("[data-mpc-redemption-pagination]");
      var loadMoreEl =
        redemptionDlgEl &&
        redemptionDlgEl.querySelector("[data-mpc-redemption-load-more]");
      if (listEl) listEl.innerHTML = "";
      if (emptyEl) emptyEl.style.display = "none";
      if (pagerEl) pagerEl.style.display = "none";
      if (loadMoreEl) setLoadMoreState(loadMoreEl, "hidden");
      if (skeletonEl) skeletonEl.style.display = "";

      openDialog(redemptionDlgEl, triggerEl);

      // 重置分页状态，请求第 1 页（initial=true → 显示骨架屏）
      redemptionPagination.page = 1;
      redemptionPagination.total = 0;
      redemptionPagination.hasMore = true;
      redemptionPagination.list = [];
      redemptionPagination.isLoading = false;
      // 先解绑旧监听（防止 dialog 关闭后再次打开时残留）
      if (listEl) detachRedemptionScrollObserver(listEl);
      loadRedemptionPage(
        1,
        listEl,
        emptyEl,
        skeletonEl,
        pagerEl,
        loadMoreEl,
        true,
      );
    }

    /**
     * 拉取指定页的兑换记录
     * - isInitial=true: 初始化 / PC 端翻页 → 显示骨架屏 + 替换列表
     * - isInitial=false: Mobile 端触底加载 → 显示 loading 指示器 + 追加到列表
     * - 错误处理：初始化失败显示空态；触底加载失败仅隐藏 loading 指示器
     */
    function loadRedemptionPage(
      pageNum,
      listEl,
      emptyEl,
      skeletonEl,
      pagerEl,
      loadMoreEl,
      isInitial,
    ) {
      if (!listEl) return;
      if (redemptionPagination.isLoading) return;
      var mobile = isMobile;
      var isMobileLoadMore = mobile && !isInitial;
      redemptionPagination.isLoading = true;

      if (isMobileLoadMore) {
        // 触底加载：显示 loading 指示器，保留已有 list
        if (loadMoreEl) setLoadMoreState(loadMoreEl, "loading");
        if (emptyEl) emptyEl.style.display = "none";
      } else {
        // 初始化或 PC 翻页：显示骨架屏，清空 list
        if (skeletonEl) skeletonEl.style.display = "";
        if (emptyEl) emptyEl.style.display = "none";
        if (pagerEl) pagerEl.style.display = "none";
        if (loadMoreEl) setLoadMoreState(loadMoreEl, "hidden");
        listEl.innerHTML = "";
      }

      redemptionPagination.page = pageNum;

      fetchRedemptionRecords(pageNum, redemptionPagination.pageSize)
        .then(function (data) {
          redemptionPagination.isLoading = false;
          if (skeletonEl) skeletonEl.style.display = "none";

          var newList = (data && data.list) || [];
          if (typeof data.total === "number") {
            redemptionPagination.total = data.total;
          }
          redemptionPagination.hasMore = !!data.hasMore;

          if (isMobileLoadMore) {
            // 移动端触底加载：追加到已有列表
            redemptionPagination.list = redemptionPagination.list.concat(
              newList,
            );
            appendRedemptionRows(listEl, newList);
            // 列表不满一屏且还有更多数据：自动加载下一批
            if (
              redemptionPagination.hasMore &&
              listEl.scrollHeight <= listEl.clientHeight
            ) {
              setTimeout(function () {
                loadRedemptionPage(
                  pageNum + 1,
                  listEl,
                  emptyEl,
                  skeletonEl,
                  pagerEl,
                  loadMoreEl,
                  false,
                );
              }, 50);
            }
            return;
          }

          // 初始化 / PC 翻页：替换列表
          redemptionPagination.list = newList;
          if (!newList.length) {
            if (pageNum === 1 && emptyEl) emptyEl.style.display = "";
            // 即便空数据，分页器可能仍需展示
            renderRedemptionPage(listEl, pagerEl);
            return;
          }
          renderRedemptionPage(listEl, pagerEl);
          // 移动端初始化后绑定滚动监听
          if (mobile) {
            attachRedemptionScrollObserver(
              listEl,
              loadMoreEl,
              emptyEl,
              skeletonEl,
              pagerEl,
            );
          }
        })
        .catch(function (err) {
          redemptionPagination.isLoading = false;
          console.warn("[MPC] Failed to load redemption records:", err);
          if (skeletonEl) skeletonEl.style.display = "none";
          // 失败时：list 已有数据就保留高度，否则隐藏
          var redemptionListHasData =
            listEl && listEl.children && listEl.children.length > 0;
          if (loadMoreEl) {
            if (mobile && redemptionListHasData) {
              setLoadMoreState(loadMoreEl, "reserved");
            } else {
              setLoadMoreState(loadMoreEl, "hidden");
            }
          }
          if (isInitial) {
            if (listEl) listEl.innerHTML = "";
            if (emptyEl) emptyEl.style.display = "";
          }
        });
    }

    /**
     * 移动端：给兑换记录列表绑定滚动监听，触底时自动加载下一页
     * 性能优化：rAF 包裹避免 layout thrash
     */
    function attachRedemptionScrollObserver(
      listEl,
      loadMoreEl,
      emptyEl,
      skeletonEl,
      pagerEl,
    ) {
      // 防止重复绑定
      if (redemptionPagination.scrollHandler) {
        listEl.removeEventListener(
          "scroll",
          redemptionPagination.scrollHandler,
        );
        redemptionPagination.scrollHandler = null;
      }
      var ticking = false;
      var handler = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          // 防止 resize 到 PC 后误触发：仅 mobile 模式使用滚动加载
          if (!isMobile) return;
          if (redemptionPagination.isLoading || !redemptionPagination.hasMore)
            return;
          // 触底检测：距离底部 < 50px 视为触底
          if (
            listEl.scrollTop + listEl.clientHeight >=
            listEl.scrollHeight - 50
          ) {
            loadRedemptionPage(
              redemptionPagination.page + 1,
              listEl,
              emptyEl,
              skeletonEl,
              pagerEl,
              loadMoreEl,
              false,
            );
          }
        });
      };
      redemptionPagination.scrollHandler = handler;
      listEl.addEventListener("scroll", handler, { passive: true });
    }

    /**
     * 移动端：解绑兑换记录列表的滚动监听
     */
    function detachRedemptionScrollObserver(listEl) {
      if (redemptionPagination.scrollHandler) {
        listEl.removeEventListener(
          "scroll",
          redemptionPagination.scrollHandler,
        );
        redemptionPagination.scrollHandler = null;
      }
    }

    /**
     * 移动端：追加兑换记录行到列表（不重建整个列表）
     */
    function appendRedemptionRows(listEl, items) {
      if (!listEl || !Array.isArray(items) || !items.length) return;
      var frag = document.createDocumentFragment();
      items.forEach(function (item) {
        var row = document.createElement("div");
        row.className = "mpc-redemption-row";

        // 多语言映射：若当前 section 配置过该 productId 的卡片，优先用 Shopify 本地化标题
        // 顺序：localizedTitle（多语言）> API productName > fallbackTitle
        var locale = productLocaleMap[String(item.shopifyProductId)] || {};
        var productName =
          locale.localizedTitle ||
          item.productName ||
          locale.fallbackTitle ||
          "";

        // 第一列：图片（与卡片规则一致，productImageNew 优先；卡片兜底图作为最后兜底）
        var productImage =
          item.productImage ||
          item.productImageNew ||
          locale.fallbackImage ||
          "";
        // alt 兜底链：localizedTitle / productName / fallbackTitle / 通用文案
        var altText = productName || "Product image";
        var imgHtml = productImage
          ? '<img class="mpc-redemption-row__img" src="' +
            escapeAttr(productImage) +
            '" alt="' +
            escapeAttr(altText) +
            '" loading="lazy" decoding="async" onerror="this.style.display=\'none\'" />'
          : '<span class="mpc-redemption-row__img-placeholder" aria-hidden="true"></span>';

        // 第二列：积分（subPoint）
        // - 若值前已有 "-"，原样使用；否则在前面补 "-"
        var subPoint = String(item.subPoint == null ? "" : item.subPoint);
        var displayPoints =
          subPoint.charAt(0) === "-" ? subPoint : "-" + subPoint;

        // 第三列：日期（createTime）
        var createTime = item.createTime || "";

        row.innerHTML =
          '<span class="mpc-redemption-row__media">' +
          imgHtml +
          "</span>" +
          '<span class="mpc-redemption-row__name">' +
          escapeHtml(productName) +
          "</span>" +
          '<span class="mpc-redemption-row__points points-val">' +
          escapeHtml(displayPoints) +
          "</span>" +
          '<span class="mpc-redemption-row__date">' +
          escapeHtml(formatMarketTime(createTime)) +
          "</span>";
        frag.appendChild(row);
      });
      listEl.appendChild(frag);
    }

    /**
     * 渲染兑换记录当前页
     * - PC 端：替换整个列表 + 渲染分页器
     * - Mobile 端（仅 initial=true 时调用）：渲染整页 + 触发滚动监听
     * 数据已由 loadRedemptionPage 写入 redemptionPagination.list。
     * 桌面端：3 列（产品图+名称堆叠 / 积分 / 日期）
     * 移动端：左列产品图，右列（产品名 + 积分/日期）
     */
    function renderRedemptionPage(listEl, pagerEl) {
      var list = redemptionPagination.list || [];
      var page = redemptionPagination.page;
      var pageSize = redemptionPagination.pageSize;
      // total 用于分页器；有 total 时用 total，否则用 "已加载的页数 * pageSize" 作为兜底
      var total =
        redemptionPagination.total ||
        page * pageSize + (redemptionPagination.hasMore ? 1 : 0);

      listEl.innerHTML = "";
      appendRedemptionRows(listEl, list);
      renderPagination(
        pagerEl,
        total,
        page,
        pageSize,
        function (newPage) {
          // 切换页码（仅 PC）→ 显示骨架屏 → 服务端拉取 → 渲染
          loadRedemptionPage(
            newPage,
            listEl,
            null,
            null,
            pagerEl,
            null,
            true,
          );
        },
      );
    }

    // --------------------------------------------------------------------------
    // 产品卡片：数据加载与渲染
    // --------------------------------------------------------------------------

    // 收集所有卡片 DOM 及配置
    var cardEls = gridEl
      ? Array.from(gridEl.querySelectorAll("[data-mpc-card]"))
      : [];
    allCards = cardEls.map(function (el) {
      return {
        el: el,
        productId: el.dataset.productId,
        localizedTitle: el.dataset.productTitle || "",
        fallbackTitle: el.dataset.fallbackTitle || "",
        fallbackImage: el.dataset.fallbackImage || "",
        fallbackPoints: Number(el.dataset.fallbackPoints) || 0,
        apiData: null, // 后续填充
      };
    });

    // 兑换记录多语言映射表：shopifyProductId -> { localizedTitle, fallbackImage }
    // 用于渲染 Redemption Record 弹框时复用卡片的多语言/兜底图配置
    // 优先级：localizedTitle（Shopify 本地化） > API productName > 兜底 fallbackTitle
    var productLocaleMap = {};
    allCards.forEach(function (card) {
      if (card.productId) {
        productLocaleMap[String(card.productId)] = {
          localizedTitle: card.localizedTitle,
          fallbackTitle: card.fallbackTitle,
          fallbackImage: card.fallbackImage,
        };
      }
    });

    // 立即折叠超出默认可见行数的卡片，避免初始化阶段 section 高度 = 全部卡片高度，
    // 造成骨架屏（仅 1 行占位）下方出现大量空白。
    // 原 setupLoadMore 仅在 fetchRedeemProducts 成功后调用，DOM 加载到 API 返回之间
    // section 已按全部卡片撑开。
    setupLoadMore();

    // 同时按"默认可见卡片数"动态补齐骨架屏的占位卡片数，
    // 让骨架屏高度 = 默认可见行数 × 行高，与真实 section 高度一致。
    syncSkeletonCardCount();

    /**
     * 根据 API 数据更新单个卡片
     * @param {{el, productId, localizedTitle, fallbackTitle, fallbackImage, fallbackPoints, apiData}} card
     */
    function updateCard(card) {
      var el = card.el;
      var data = card.apiData;
      if (!data) {
        // API 未返回该产品，隐藏卡片
        el.setAttribute("data-hidden", "");
        return;
      }
      // 产品图优先级：productImageNew → productImage（旧的） → 兜底配置图
      var imgWrap = el.querySelector(".mpc-product-card__img-wrap");
      // 产品名优先级：Shopify 本地化标题（多语言）→ API productName → 兜底配置
      var productTitle =
        card.localizedTitle || data.productName || card.fallbackTitle;
      var productImage =
        data.productImage || data.productImageNew || card.fallbackImage;
      if (imgWrap) {
        if (productImage) {
          // alt 兜底链：localizedTitle / productName / fallbackTitle / 通用文案
          var altText =
            productTitle || card.fallbackTitle || "Product image";
          var img = el.querySelector(".mpc-product-card__img");
          if (img) {
            // srcset 路径与 src 保持一致
            img.src = productImage;
            img.srcset = productImage;
            img.alt = altText;
            // 性能：异步解码 + 显式宽高减少 CLS（仅在尚未设置时设置，避免覆盖）
            if (!img.hasAttribute("decoding")) img.decoding = "async";
            if (!img.hasAttribute("width")) img.setAttribute("width", "300");
            if (!img.hasAttribute("height")) img.setAttribute("height", "300");
          } else {
            imgWrap.innerHTML =
              '<img class="mpc-product-card__img" src="' +
              escapeAttr(productImage) +
              '" srcset="' +
              escapeAttr(productImage) +
              '" alt="' +
              escapeHtml(altText) +
              '" loading="lazy" decoding="async" width="300" height="300">';
          }
        }
      }

      // 标题
      var titleEl = el.querySelector(".mpc-product-card__title");
      if (titleEl) titleEl.textContent = productTitle;
      // 缓存 productTitle 到按钮上，便于 setCardBtnDisabled 拼 aria-label
      var btnRef = el.querySelector("[data-mpc-redeem-btn]");
      if (btnRef) btnRef.__mpcProductTitle = productTitle;

      // 积分（API 优先；后台无值时使用 block 配置的兜底积分）
      var apiPoints =
        typeof data.exchangePoint === "number" && !isNaN(data.exchangePoint)
          ? data.exchangePoint
          : null;
      var resolvedPoints =
        apiPoints != null ? apiPoints : card.fallbackPoints || 0;
      var pointsNumEl = el.querySelector("[data-mpc-card-points]");
      if (pointsNumEl)
        pointsNumEl.textContent =
          resolvedPoints > 0 ? resolvedPoints.toLocaleString() : "—";

      // 存储产品积分到 DOM
      el.dataset.requiredPoints = resolvedPoints;
      // 产品编码（后台返回的 productCode），挂到卡片 data 属性上
      el.dataset.productCode =
        data.productCode !== undefined && data.productCode !== null
          ? String(data.productCode)
          : "";
      // 库存 / 兑换次数相关数据属性绑定到卡片上
      el.dataset.productCount =
        typeof data.productCount === "number" ? data.productCount : 0;
      el.dataset.usedCount =
        typeof data.usedCount === "number" ? data.usedCount : 0;
      el.dataset.userLimitCount =
        typeof data.userLimitCount === "number" ? data.userLimitCount : 0;
      // 兑换上限是否已到达：已兑换数量 >= 用户兑换上限次数
      el.dataset.redeemLimitReached =
        el.dataset.usedCount >= el.dataset.userLimitCount ? "1" : "0";
      // 兑换状态枚举：1=可兑换 2=积分不足 4=库存不足 7=已达兑换上限
      el.dataset.redemptionStatus =
        data.redemptionStatus !== undefined && data.redemptionStatus !== null
          ? String(data.redemptionStatus)
          : "1";

      // 按钮状态
      refreshSingleCardBtn(card);
    }

    /**
     * 刷新单个卡片按钮状态
     * 根据 redemptionStatus 枚举判断：
     *   1 = 可兑换 → 按钮激活，文案 "Redeem"
     *   2 = 积分不足 → 按钮置灰不可点击，文案保持 "Redeem"
     *   4 = 库存不足 → 按钮置灰不可点击，文案显示 "Out of Stock"
     *   7 = 已达兑换上限 → 按钮置灰不可点击，文案保持 "Redeem"
     */
    function refreshSingleCardBtn(card) {
      var el = card.el;
      var btn = el.querySelector("[data-mpc-redeem-btn]");
      if (!btn) return;

      var status = parseInt(el.dataset.redemptionStatus, 10);

      // 0: productCode 缺失（API 未返回 productCode 字段或该 product 在 pointProductRespList 中没匹配）
      // - 优先使用 productCode（新版兑换 API）
      // - 缺失时 fallback 到 productId（Shopify productId，liquid 模板硬编码）
      // - 这样即使后端数据不全，至少按钮可点击、兑换走原逻辑
      if (!el.dataset.productCode) {
        console.warn(
          "[MPC] Card",
          el.dataset.productId,
          "is missing productCode; will fallback to productId for the redeem API.",
        );
        // 不禁用按钮，继续走 setCardBtnActive
      }

      // 4: 库存不足
      if (status === 4) {
        setCardBtnDisabled(btn, cardOutOfStockLabel);
        return;
      }

      // 2: 积分不足
      if (status === 2) {
        setCardBtnDisabled(btn, cardRedeemLabel);
        return;
      }

      // 7: 已达兑换上限
      if (status === 7) {
        setCardBtnDisabled(btn, cardRedeemLabel);
        return;
      }

      // 兜底：任何未识别的 redemptionStatus（非 1/2/3/4/7）一律视为不可兑换
      // 避免按钮看起来可点但点了"无反应"（click handler 的 status !== 1 早退会静默拦截）
      if (status !== 1) {
        console.warn(
          "[MPC] Card",
          el.dataset.productId,
          "has unrecognized redemptionStatus:",
          status,
          "- disabling button.",
        );
        setCardBtnDisabled(btn, cardRedeemLabel);
        return;
      }

      // 1: 可兑换 / 未登录可点击
      setCardBtnActive(btn, cardRedeemLabel);
    }

    function setCardBtnActive(btn, label) {
      btn.textContent = label;
      btn.classList.remove("mpc-product-card__btn--disabled");
      btn.classList.add("mpc-product-card__btn--active");
      btn.disabled = false;
      // 移除 disabled 态的 ARIA 状态，保留普通语义
      btn.removeAttribute("aria-disabled");
      if (btn.__mpcPrevAriaLabel !== undefined && btn.__mpcPrevAriaLabel !== null) {
        // 还原被 setCardBtnDisabled 覆盖前的 aria-label
        if (btn.__mpcPrevAriaLabel) {
          btn.setAttribute("aria-label", btn.__mpcPrevAriaLabel);
        } else {
          btn.removeAttribute("aria-label");
        }
      }
      btn.__mpcPrevAriaLabel = null;
    }

    function setCardBtnDisabled(btn, label) {
      btn.textContent = label;
      btn.classList.add("mpc-product-card__btn--disabled");
      btn.classList.remove("mpc-product-card__btn--active");
      btn.disabled = true;
      // 同步 ARIA 状态：aria-disabled 让屏幕阅读器知道按钮已禁用
      btn.setAttribute("aria-disabled", "true");
      // 显式 aria-label 描述禁用原因，便于 SR 用户理解
      // 记录先前 aria-label（JS expando 属性，不写入 HTML 属性）以便恢复
      btn.__mpcPrevAriaLabel = btn.getAttribute("aria-label") || "";
      var reason = label === cardOutOfStockLabel ? "Out of stock" : label;
      var productName =
        btn.__mpcProductTitle || btn.getAttribute("data-mpc-product-name") || "";
      btn.setAttribute(
        "aria-label",
        (productName ? productName + " — " : "") + reason + " (unavailable)",
      );
    }

    /**
     * 批量刷新所有卡片按钮状态
     */
    function refreshCardButtonStates() {
      allCards.forEach(function (card) {
        if (!card.el.hasAttribute("data-hidden")) {
          refreshSingleCardBtn(card);
        }
      });
    }

    // 加载产品数据
    function loadProducts() {
      if (!productIds.length) {
        // 没有产品时直接隐藏骨架屏
        hideSectionPageSkeleton(sectionEl);
        return;
      }
      fetchRedeemProducts()
        .then(function (res) {
          const { code, result } = res;
          if (code === "200") {
            // 同一接口返回 result.points（用户总积分）— 顺手更新积分余额
            if (result && typeof result.points === "number") {
              userTotalPoints = result.points;
              if (pointsValueEl)
                pointsValueEl.textContent = userTotalPoints.toLocaleString();
              var dlgPointsEl = document.querySelector(
                "#cozycoins-dialog-" + sectionId + " [data-mpc-dialog-points]",
              );
              if (dlgPointsEl)
                dlgPointsEl.textContent = userTotalPoints.toLocaleString();
            }
            const { pointProductRespList = [] } = result ?? {};
            var productIdSet = productIds.map(function (id) {
              return String(id);
            });
            // 按 shopifyProductId 过滤出 productIds 中需要展示的实物礼品
            var filteredList = (
              Array.isArray(pointProductRespList) ? pointProductRespList : []
            ).filter(function (item) {
              return (
                item &&
                item.shopifyProductId &&
                productIdSet.indexOf(String(item.shopifyProductId)) !== -1
              );
            });
            // 建立 shopifyProductId -> apiData 映射
            var apiMap = {};
            filteredList.forEach(function (item) {
              if (item && item.shopifyProductId)
                apiMap[String(item.shopifyProductId)] = item;
            });
            allCards.forEach(function (card) {
              card.apiData = apiMap[String(card.productId)] || null;
              // 把 productDesc 同步落到 DOM dataset，供确认弹框读取
              if (card.apiData && typeof card.apiData.productDesc === "string") {
                card.el.dataset.productDesc = card.apiData.productDesc;
              } else {
                card.el.removeAttribute("data-product-desc");
              }
              updateCard(card);
            });
            // 卡片数据与 DOM 装载完成后，再隐藏页面骨架屏
            hideSectionPageSkeleton(sectionEl);
            // 初始化 Load More
            setupLoadMore();
          } else {
            console.warn("[MPC] Failed to load redeem products:", res);
            // 接口返回非 200 时也隐藏骨架屏
            hideSectionPageSkeleton(sectionEl);
          }
        })
        .catch(function (err) {
          console.warn("[MPC] Failed to load redeem products:", err);
          // 接口失败时也隐藏骨架屏，让用户看到兜底内容
          hideSectionPageSkeleton(sectionEl);
        });
    }

    // --------------------------------------------------------------------------
    // Load More 逻辑
    // --------------------------------------------------------------------------

    function setupLoadMore() {
      isMobile = window.innerWidth <= 768;
      var colsPerRow = isMobile ? 2 : 4;
      var defaultRows = isMobile ? loadMoreRows.mobile : loadMoreRows.pc;
      var maxVisible = colsPerRow * defaultRows;

      // 只统计可见（非隐藏）的卡片
      var visibleCards = allCards.filter(function (c) {
        return !c.el.hasAttribute("data-hidden");
      });

      visibleCards.forEach(function (card, idx) {
        if (idx >= maxVisible) {
          card.el.classList.add("mpc-hidden");
        } else {
          card.el.classList.remove("mpc-hidden");
        }
      });

      if (visibleCards.length > maxVisible) {
        if (loadMoreWrapEl) loadMoreWrapEl.style.display = "";
        isLoadMoreVisible = true;
      } else {
        if (loadMoreWrapEl) loadMoreWrapEl.style.display = "none";
        isLoadMoreVisible = false;
      }
    }

    /**
     * 按"默认可见行数"动态补齐/裁剪骨架屏的占位卡片数，
     * 让骨架屏高度与真实 section 折叠后的高度保持一致。
     * 仅在骨架屏还可见时（data-mpc-ready !== "true"）才执行。
     *
     * 直接以 wantCapped（defaultRows × cols，按真实可见卡片数封顶）作为目标，
     * 但需要用"真实产品网格"的 offsetHeight 二次封顶：
     * 移动端骨架卡片强制 aspect-ratio: 1/1，比真实卡片（img height: auto）更高，
     * 若 wantCapped 行骨架总高 > 真实网格可用高度，section 高度由真实内容决定，
     * 骨架会被 overflow: hidden 裁掉最后一行。
     */
    function syncSkeletonCardCount() {
      try {
        if (!sectionEl || sectionEl.getAttribute("data-mpc-ready") === "true") {
          return;
        }
        var skelGrid = sectionEl.querySelector(
          ".mpc-page-skeleton__grid",
        );
        if (!skelGrid) return;
        var skel = skelGrid.closest(".mpc-page-skeleton");
        var realGrid = sectionEl.querySelector(".mpc-products__grid");
        var mobile = window.innerWidth <= 768;
        var cols = mobile ? 2 : 4;
        var rows = mobile ? loadMoreRows.mobile : loadMoreRows.pc;
        var want = cols * rows;
        // 也按 allCards 实际可见行数封顶，避免 want 大于真实卡片数时
        // 骨架屏仍占多余空白（与折叠后的真实高度不匹配）
        var realVisible = allCards.filter(function (c) {
          return !c.el.classList.contains("mpc-hidden");
        }).length;
        var wantCapped = Math.max(1, Math.min(want, realVisible || want));

        // 兜底：限制骨架内容溢出覆盖到下一节。
        if (skel && !skel.style.overflow) {
          skel.style.overflow = "hidden";
        }

        // 关键：rAF 测量后一次性按 wantCapped 设置 target（按 cols 对齐的完整行），
        // 避免"先按 want 增加到 N 行 → overflow: hidden 裁掉底部 → rAF 后再回退"
        // 过程中用户看到"最后一行的骨架被截断"。
        requestAnimationFrame(function () {
          if (
            !sectionEl ||
            sectionEl.getAttribute("data-mpc-ready") === "true"
          )
            return;
          if (!firstCardReady(skelGrid)) return;
          var target = Math.max(cols, Math.floor(wantCapped / cols) * cols);
          // 之前用 realGrid.offsetHeight 二次封顶，但 rAF 时机下真实卡片图片
          // 可能还没加载完，realGrid.offsetHeight 偏小、误把 target 砍小。
          // 现在直接用 wantCapped，宁可让骨架多渲染几行（被 overflow: hidden 裁掉底部），
          // 也不要在初始化阶段留下大块底部空白。
          applySkeletonTarget(skelGrid, target);
        });
      } catch (e) {
        /* noop */
      }
    }

    /**
     * 判断骨架 grid 至少有一张已布局的卡片（offsetHeight > 0），
     // 避免 rAF 时机过早导致测量值为 0。
     */
    function firstCardReady(skelGrid) {
      var first = skelGrid && skelGrid.firstElementChild;
      return !!(first && first.offsetHeight > 0);
    }

    /**
     * 把骨架 grid 调整到指定卡片数（按 cols 对齐的完整行）。
     * 不立即按 want 加满，避免一帧内被 overflow: hidden 裁剪。
     */
    function applySkeletonTarget(skelGrid, target) {
      if (!skelGrid) return;
      var current = skelGrid.children.length;
      if (current === target) return;
      if (current < target) {
        var tpl = skelGrid.firstElementChild;
        if (!tpl) return;
        for (var i = current; i < target; i++) {
          skelGrid.appendChild(tpl.cloneNode(true));
        }
      } else {
        while (skelGrid.children.length > target) {
          skelGrid.removeChild(skelGrid.lastElementChild);
        }
      }
    }

    if (loadMoreBtnEl) {
      loadMoreBtnEl.addEventListener("click", function () {
        // 显示所有卡片
        allCards.forEach(function (card) {
          card.el.classList.remove("mpc-hidden");
        });
        if (loadMoreWrapEl) loadMoreWrapEl.style.display = "none";
        isLoadMoreVisible = false;
      });
    }

    // 窗口尺寸变化时重新计算
    window.addEventListener(
      "resize",
      debounce(function () {
        var wasMobile = isMobile;
        isMobile = window.innerWidth <= 768;
        if (wasMobile !== isMobile && !isLoadMoreVisible) {
          // 如果 load more 已展开，不重新折叠
        } else if (wasMobile !== isMobile) {
          setupLoadMore();
        }
      }, 200),
    );

    // --------------------------------------------------------------------------
    // 兑换按钮点击
    // --------------------------------------------------------------------------

    if (gridEl) {
      console.log("[MPC] gridEl found, binding click handler", gridEl);
      gridEl.addEventListener("click", function (e) {
        console.warn("[MPC] click handler fired", e.target);
        var btn = e.target.closest("[data-mpc-redeem-btn]");
        if (!btn) {
          console.warn("[MPC] click: no [data-mpc-redeem-btn] in target chain");
          return;
        }
        if (btn.disabled) {
          console.log("[MPC] Click: btn is disabled");
          return;
        }

        var cardEl = btn.closest("[data-mpc-card]");
        if (!cardEl) {
          console.log("[MPC] Click: no card found");
          return;
        }

        // productCode 是后台返回的商品编码（用于兑换 API），不是 Shopify 的 productId
        // - 优先用 productCode（新版 API）
        // - 缺失时 fallback 到 productId（liquid 硬编码，原版 API 用它）
        var productCode =
          cardEl.dataset.productCode || cardEl.dataset.productId || "";
        var requiredPoints = parseInt(cardEl.dataset.requiredPoints, 10) || 0;
        var status = parseInt(cardEl.dataset.redemptionStatus, 10);

        console.warn("[MPC] Click card", cardEl.dataset.productId, {
          productCode: productCode,
          requiredPoints: requiredPoints,
          status: status,
          dataAttrs: {
            productCode: cardEl.dataset.productCode,
            productId: cardEl.dataset.productId,
            requiredPoints: cardEl.dataset.requiredPoints,
            redemptionStatus: cardEl.dataset.redemptionStatus,
          },
          apiData: (
            allCards.find(function (c) {
              return c.el === cardEl;
            }) || {}
          ).apiData,
          isLoggedIn: isLoggedIn(),
        });

        // 仅 redemptionStatus === 1（可兑换）才允许进入兑换流程
        if (status !== 1) {
          console.log("[MPC] Click: status is not 1, skip");
          return;
        }

        // 用户未登录：弹出登录弹框
        if (!isLoggedIn()) {
          console.log("[MPC] Click: not logged in, opening login dialog");
          openLoginDialog(registerSource);
          return;
        }

        // 用户已登录：先重新查询最新积分，确认是否满足
        fetchUserPoints("redeem")
          .then(function (data) {
            var latestPoints =
              data && typeof data.total === "number" ? data.total : 0;
            console.log("[MPC] fetchUserPoints returned", latestPoints, "need", requiredPoints);

            // 先判断积分是否足够（不更新 UI，按用户要求）
            if (latestPoints < requiredPoints) {
              console.log("[MPC] Click: insufficient points, showing dialog");
              // 积分不足 → 弹出积分不足提示
              // ⚠️ 关键：此处不更新 UI（banner / 按钮状态保持原样）
              showInsufficientDialog(requiredPoints, latestPoints, cardEl);
              return;
            }

            // 积分满足 → 先更新内存与 UI，再弹出确认弹框
            userTotalPoints = latestPoints;
            if (pointsValueEl)
              pointsValueEl.textContent = userTotalPoints.toLocaleString();
            refreshCardButtonStates();

            // 再次校验兑换状态（防止并发场景下状态已变更）
            var latestStatus = parseInt(cardEl.dataset.redemptionStatus, 10);
            if (latestStatus !== 1) {
              console.log("[MPC] Click: latestStatus not 1, refreshing");
              refreshSingleCardBtn(
                allCards.find(function (c) {
                  return c.el === cardEl;
                }) || { el: cardEl },
              );
              return;
            }

            console.log("[MPC] Click: opening confirm dialog for", productCode);
            // 弹出确认弹框
            openConfirmDialog(cardEl, productCode, requiredPoints);
          })
          .catch(function (err) {
            console.warn("[MPC] fetchUserPoints failed, fallback to cache:", err);
            // 查询失败时仍用本地缓存值判断（不更新 UI）
            if (userTotalPoints < requiredPoints) {
              showInsufficientDialog(requiredPoints, userTotalPoints, cardEl);
            } else {
              openConfirmDialog(cardEl, productCode, requiredPoints);
            }
          });
      });
    }

    // --------------------------------------------------------------------------
    // 积分不足弹框
    // --------------------------------------------------------------------------

    function showInsufficientDialog(need, have, triggerEl) {
      var msgEl =
        insufficientDlgEl &&
        insufficientDlgEl.querySelector("[data-mpc-insufficient-msg]");
      if (msgEl) {
        var msg = insufficientMsgTpl
          .replace("{need}", need.toLocaleString())
          .replace("{have}", have.toLocaleString());
        msgEl.textContent = msg;
      }
      openDialog(insufficientDlgEl, triggerEl);
    }

    // --------------------------------------------------------------------------
    // 兑换确认弹框
    // --------------------------------------------------------------------------

    function openConfirmDialog(cardEl, productCode, requiredPoints) {
      currentRedeemProductCode = productCode;
      currentRedeemProductPoints = requiredPoints;

      // 填充产品视觉区域（背景图 + 产品图）
      var visualBgEl =
        confirmDlgEl && confirmDlgEl.querySelector("[data-mpc-confirm-bg]");
      var visualImgEl =
        confirmDlgEl && confirmDlgEl.querySelector("[data-mpc-confirm-img]");

      // 取卡片上部的背景图和产品图
      var cardTopBgEl = cardEl.querySelector(".mpc-product-card__top-bg");
      var cardImgEl = cardEl.querySelector(".mpc-product-card__img");

      if (visualBgEl && cardTopBgEl) {
        visualBgEl.style.backgroundImage = cardTopBgEl.style.backgroundImage;
      }

      if (visualImgEl) {
        visualImgEl.innerHTML = "";
        if (cardImgEl) {
          var clonedImg = cardImgEl.cloneNode(false);
          clonedImg.removeAttribute("loading");
          visualImgEl.appendChild(clonedImg);
        }
      }

      // 填充确认文案：优先用卡片 dataset.productDesc，缺值则用 section 注入的 st.confirm_rich_text 兜底
      var richTextEl =
        confirmDlgEl && confirmDlgEl.querySelector("[data-mpc-confirm-rich-text]");
      if (richTextEl) {
        var productDesc = cardEl.dataset.productDesc;
        var fallback =
          (typeof confirmRichTextFallback === "string" && confirmRichTextFallback) ||
          richTextEl.innerHTML ||
          "";
        if (productDesc) {
          richTextEl.textContent = productDesc;
        } else if (fallback) {
          richTextEl.innerHTML = fallback;
        }
      }

      // 关闭时还原焦点到卡片上的兑换按钮
      var redeemBtn = cardEl.querySelector("[data-mpc-redeem-btn]");
      openDialog(confirmDlgEl, redeemBtn || cardEl);
    }

    // 确认按钮点击
    var confirmBtn =
      confirmDlgEl && confirmDlgEl.querySelector("[data-mpc-confirm-btn]");
    if (confirmBtn) {
      confirmBtn.addEventListener("click", function () {
        if (!currentRedeemProductCode) return;
        confirmBtn.disabled = true;
        confirmBtn.textContent = "Processing...";

        // number=1（默认兑换数量），usedPoints=卡片的 exchangePoint
        doRedeem(currentRedeemProductCode, 1, currentRedeemProductPoints)
          .then(function (data) {
            console.log("[MPC] doRedeem success:", data);
            // 兑换成功：
            //   1. 关闭 confirm 弹框
            //   2. 显示成功 toast
            //   3. 刷新积分余额
            //   4. 若 result.jumpUrl 存在 → 当前窗口跳转到 jumpUrl
            closeDialog(confirmDlgEl);
            confirmBtn.disabled = false;
            confirmBtn.textContent = confirmBtnLabel;
            // 刷新积分
            loadUserPoints();
            // 显示成功 toast
            showToast(toastSuccessContent, true);

            // 跳转到兑换成功页（jumpUrl 由后端返回）
            var jumpUrl =
              data && data.result && typeof data.result.jumpUrl === "string"
                ? data.result.jumpUrl.trim()
                : "";
            if (jumpUrl) {
              // 使用 window.location.href 在当前窗口跳转（替换历史而非新窗口）
              window.location.href = jumpUrl;
            }
          })
          .catch(function (err) {
            console.warn("[MPC] doRedeem failed:", err);
            confirmBtn.disabled = false;
            confirmBtn.textContent = confirmBtnLabel;
            // 优先根据 serverCode 在 redeemErrorMessages 映射中查找友好文案
            // 兜底顺序：err.serverMsg → err.message → 通用兜底
            var code = err && err.serverCode !== undefined ? err.serverCode : null;
            var mappedMsg =
              code !== null && redeemErrorMessages[String(code)]
                ? redeemErrorMessages[String(code)]
                : null;
            var errMsg =
              mappedMsg ||
              (err && err.serverMsg) ||
              (err && err.message) ||
              "An error occurred.";
            showToast(errMsg, false);
          });
      });
    }

    // --------------------------------------------------------------------------
    // 时间格式化（按市场时区）
    // --------------------------------------------------------------------------
    // market → IANA 时区映射（market 来自 window.__MC__CONFIG__.market，
    // 即 localization.country.iso_code 小写，如 "us"/"de"/"jp"）
    var MARKET_TIMEZONE_MAP = {
      us: "America/New_York",
      ca: "America/Toronto",
      mx: "America/Mexico_City",
      co: "America/Bogota",
      gb: "Europe/London",
      uk: "Europe/London",
      ie: "Europe/Dublin",
      de: "Europe/Berlin",
      fr: "Europe/Paris",
      es: "Europe/Madrid",
      it: "Europe/Rome",
      pt: "Europe/Lisbon",
      nl: "Europe/Amsterdam",
      be: "Europe/Brussels",
      pl: "Europe/Warsaw",
      ch: "Europe/Zurich",
      at: "Europe/Vienna",
      se: "Europe/Stockholm",
      no: "Europe/Oslo",
      dk: "Europe/Copenhagen",
      fi: "Europe/Helsinki",
      cz: "Europe/Prague",
      gr: "Europe/Athens",
      ru: "Europe/Moscow",
      tr: "Europe/Istanbul",
      sa: "Asia/Riyadh",
      ae: "Asia/Dubai",
      il: "Asia/Jerusalem",
      qa: "Asia/Qatar",
      kw: "Asia/Kuwait",
      bh: "Asia/Bahrain",
      om: "Asia/Muscat",
      jo: "Asia/Amman",
      lb: "Asia/Beirut",
      eg: "Africa/Cairo",
      za: "Africa/Johannesburg",
      ng: "Africa/Lagos",
      ke: "Africa/Nairobi",
      ma: "Africa/Casablanca",
      gh: "Africa/Accra",
      in: "Asia/Kolkata",
      pk: "Asia/Karachi",
      bd: "Asia/Dhaka",
      th: "Asia/Bangkok",
      vn: "Asia/Ho_Chi_Minh",
      my: "Asia/Kuala_Lumpur",
      sg: "Asia/Singapore",
      ph: "Asia/Manila",
      id: "Asia/Jakarta",
      kr: "Asia/Seoul",
      jp: "Asia/Tokyo",
      cn: "Asia/Shanghai",
      hk: "Asia/Hong_Kong",
      tw: "Asia/Taipei",
      au: "Australia/Sydney",
      nz: "Pacific/Auckland",
      br: "America/Sao_Paulo",
      ar: "America/Argentina/Buenos_Aires",
      cl: "America/Santiago",
      pe: "America/Lima",
    };

    /**
     * 按 window.__MC__CONFIG__.market 对应的时区把时间戳格式化为 "YYYY-MM-DD HH:MM:SS"
     * @param {number|string|Date|null|undefined} value 后台返回的时间戳（秒或毫秒）
     * @returns {string} 格式化后的时间字符串；输入无效时返回空字符串
     */
    function formatMarketTime(value) {
      if (value === null || value === undefined || value === "") return "";
      var ts = typeof value === "number" ? value : Number(value);
      if (!isFinite(ts)) return String(value);
      // 自动识别秒/毫秒：< 1e12 视为秒
      if (ts < 1e12) ts = ts * 1000;
      try {
        var market = "";
        try {
          market = (window.__MC__CONFIG__ && window.__MC__CONFIG__.market) || "";
        } catch (e) {}
        var timeZone = MARKET_TIMEZONE_MAP[String(market).toLowerCase()] || undefined;
        var parts = new Intl.DateTimeFormat("en-CA", {
          timeZone: timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).formatToParts(new Date(ts));
        var map = {};
        for (var i = 0; i < parts.length; i++) {
          map[parts[i].type] = parts[i].value;
        }
        var hh = map.hour === "24" ? "00" : map.hour || "00";
        return (
          map.year + "-" + map.month + "-" + map.day + " " +
          hh + ":" + (map.minute || "00") + ":" + (map.second || "00")
        );
      } catch (e) {
        // 兜底：返回 ISO 前 19 位
        var s = new Date(ts).toISOString();
        return s.slice(0, 19).replace("T", " ");
      }
    }

    // --------------------------------------------------------------------------
    // 安全转义
    // --------------------------------------------------------------------------
    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    // 转义 HTML 属性值（专门用于 src/href 等，防止 URL 注入）
    function escapeAttr(str) {
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
        .replace(/</g, "&lt;");
    }

    // --------------------------------------------------------------------------
    // 启动
    // --------------------------------------------------------------------------
    initLoginState();
    loadProducts();
  }

  // --------------------------------------------------------------------------
  // DOMContentLoaded 启动
  // --------------------------------------------------------------------------
  function init() {
    var sections = document.querySelectorAll(".mpc-section");
    sections.forEach(function (sectionEl) {
      initSection(sectionEl);
    });
  }

  /**
   * 隐藏指定 section 的页面骨架屏（在卡片数据 + DOM 装载完成后调用）
   *   1. 设 data-mpc-ready="true" 触发淡出动画
   *   2. 350ms 后彻底移除 DOM 节点，释放内存
   * @param {HTMLElement} sectionEl
   */
  function hideSectionPageSkeleton(sectionEl) {
    try {
      if (!sectionEl) return;
      sectionEl.setAttribute("data-mpc-ready", "true");
      var skel = sectionEl.querySelector("[data-mpc-page-skeleton]");
      if (skel) {
        setTimeout(function () {
          if (skel && skel.parentNode) skel.parentNode.removeChild(skel);
        }, 350);
      }
    } catch (e) {
      /* noop */
    }
  }

  /**
   * 标记 section 为 ready + 移除骨架屏（防白屏）
   * 内联关键 CSS 默认显示页面骨架（position: absolute, inset: 0），
   * 这里在 init 开始时：
   *   1. 设 data-mpc-ready="true" 触发淡出动画
   *   2. 300ms 后彻底移除 DOM 节点，释放内存
   * @deprecated 骨架屏的隐藏由 hideSectionPageSkeleton 在卡片数据装载完成后处理，
   *             避免页面骨架在产品接口返回前就提前消失。
   */
  function markAllSectionsReady() {
    // 不再在初始化时统一隐藏骨架屏；
    // 改为在每个 section 的 loadProducts() 中，于卡片数据 / DOM 装载完成后再隐藏。
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }
})();
